import { ThemeEvents, CartUpdateEvent, CartErrorEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';
import { validateAddToCartQuantity, getQuantityMaxErrorMessage } from '@theme/cart-line-quantity-limit';
import { getCartItemsSectionIds } from '@theme/cart-add-perf';
import { seedCartCache, isCartJsonObject } from '@theme/cart-api';

/** @param {Record<string, unknown>[]} items */
function buildQuickAddCartPayload(items) {
  /** @type {Record<string, unknown>} */
  const payload = { items };
  const sectionIds = getCartItemsSectionIds();
  if (sectionIds.length) {
    payload.sections = sectionIds.join(',');
  }
  return payload;
}

/**
 * Line item properties for the JSON /cart/add paths.
 *
 * These build their body in JS rather than serialising a form, so they miss the hidden inputs
 * product-form-item-source renders. `_auctionCode` is the only thing AMS routes on - without it a
 * missed-auction item added from a card is attributed to FPC instead of Missed - so it is carried
 * per-variant on data-auction-code and picked up here.
 *
 * An empty code means a plain FPC line and sends no property at all. Never send an empty
 * `_auctionCode`: the property would still exist on the line, and the middleware SP distinguishes
 * present from absent. Liquid leaves the attribute empty both when the variant has no code and when
 * missed-auction-code suppresses it because the item is currently on air.
 *
 * @param {Element} trigger - the clicked element, used to resolve Rebuy slider attribution
 * @param {string | null | undefined} auctionCode - data-auction-code for the variant being added
 * @returns {Record<string, string | null>}
 */
function buildQuickAddProperties(trigger, auctionCode) {
  const code = (auctionCode ?? '').trim();

  /** @type {Record<string, string | null>} */
  const properties = { _itemSource: code ? 'MA' : 'FPC' };
  if (code) properties._auctionCode = code;

  const rebuySlider = trigger.closest('.rebuy-product-slider');
  if (rebuySlider) {
    properties._source = rebuySlider.getAttribute('data-product-item-source');
    properties._widget_id = rebuySlider.getAttribute('data-product-item-widget_id');
    properties._attribution = rebuySlider.getAttribute('data-product-item-attribution');
  }

  return properties;
}

/**
 * Blocks quick-add when variant is already at the line max.
 * @param {string} variantId
 * @param {string} [sourceId]
 * @returns {Promise<boolean>}
 */
async function guardQuickAddQuantity(variantId, sourceId = 'quick-add') {
  const validation = await validateAddToCartQuantity(variantId, 1);
  if (!validation.canAdd && validation.maxQuantity != null) {
    document.dispatchEvent(
      new CartErrorEvent(sourceId, getQuantityMaxErrorMessage(validation.maxQuantity), '', {})
    );
    return false;
  }
  return true;
}

/** Mount the modal trigger over the image, including dynamically rendered cards. */
export class QuickAdd extends HTMLElement {
  connectedCallback() {
    // Wait until streamed/custom-element markup has its gallery and button children.
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.toggleAttribute('data-mobile-enabled', getComputedStyle(this).getPropertyValue('--quick-add-mobile-display').trim() === 'block');
      const card = this.closest('product-card, .product-card');
      const gallery = card?.querySelector('.card-gallery');
      if (!gallery || this.parentElement === gallery) return;
      const previousParent = this.parentElement;
      gallery.appendChild(this);
      if (previousParent?.classList.contains('product-card__footer') && !previousParent.children.length) {
        previousParent.remove();
      }
    });
  }
}

if (!customElements.get('quick-add')) {
  customElements.define('quick-add', QuickAdd);
}

/**
 * Document-level handler for "Add to Bag" alternate CTA (when quick add is disabled on CX product slider).
 * Dispatches CartUpdateEvent so header/cart drawer update.
 */
function handleAltCtaAddClick(event) {
  if (!(event.target instanceof Element)) return;
  const btn = event.target.closest('.product-card__alt-cta-add');
  if (!(btn instanceof HTMLButtonElement) || btn.disabled || !btn.hasAttribute('data-variant-id')) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const variantId = btn.getAttribute('data-variant-id');
  if (!variantId) return;

  btn.disabled = true;
  btn.classList.add('product-card__quick-add-button--loading');
  btn.setAttribute('aria-busy', 'true');

  const properties = buildQuickAddProperties(event.target, btn.getAttribute('data-auction-code'));

  guardQuickAddQuantity(variantId, 'alt-cta').then((allowed) => {
    if (!allowed) {
      btn.classList.remove('product-card__quick-add-button--loading');
      btn.setAttribute('aria-busy', 'false');
      if (!btn.classList.contains('product-card__quick-add-button--added')) btn.disabled = false;
      return;
    }

  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildQuickAddCartPayload([{ id: variantId, quantity: 1, properties: properties }]))
  })
    .then(async (response) => {
      const data = await response.json().catch(() => ({}));
      const ok = response.ok && !(data && typeof data === 'object' && data.status);
      return { ok, data };
    })
    .then(({ ok, data }) => {
      if (!ok) return;
      if (isCartJsonObject(data)) {
        seedCartCache(data);
      }
      document.dispatchEvent(
        new CartUpdateEvent(data ?? {}, 'alt-cta', {
          action: 'add',
          source: 'quick-add',
          itemCount: typeof data?.item_count === 'number' ? data.item_count : undefined,
          sections: data?.sections,
        })
      );
      if (typeof window.saveSharedCartToken === 'function') {
        void window.saveSharedCartToken();
      }
      // Keep original button text (e.g. "Quick Add"); do not switch to "Added"
    })
    .catch((err) => console.error('Error adding to cart:', err))
    .finally(() => {
      btn.classList.remove('product-card__quick-add-button--loading');
      btn.setAttribute('aria-busy', 'false');
      if (!btn.classList.contains('product-card__quick-add-button--added')) btn.disabled = false;
    });
  });
}

document.addEventListener('click', handleAltCtaAddClick, true);

// Keep QuickAddDialog for modal functionality
class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
